import React, { useState } from "react";
import type { Partner, Heat, Stage } from "../types";
import { STAGES } from "../types";
import { I } from "./Icons";

function clsx(...args: (string | boolean | undefined | null)[]): string {
  return args.filter(Boolean).join(" ");
}

export { clsx };

export function fmtDate(iso: string, opts?: Intl.DateTimeFormatOptions): string {
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  return d.toLocaleDateString("en-GB", opts || { day: "numeric", month: "short" });
}

export function daysFromToday(iso: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(iso + "T00:00:00");
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

export function relDue(iso: string): string {
  const n = daysFromToday(iso);
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n < 0) return Math.abs(n) + "d overdue";
  if (n < 7) return "in " + n + "d";
  return fmtDate(iso);
}

export function dueText(t: { due: string | null; due_label: string | null }): string {
  if (t.due) return relDue(t.due);
  return t.due_label || "—";
}

export function dueOverdue(t: { due: string | null }): boolean {
  return t.due ? daysFromToday(t.due) < 0 : false;
}

export function Avatar({ partner, size = 26, title: showTitle = true }: { partner: Partner | null; size?: number; title?: boolean }) {
  const [err, setErr] = useState(false);
  if (!partner) return null;
  const style = { width: size, height: size, fontSize: Math.round(size * 0.38) };
  if (partner.photo && !err) {
    return <img src={partner.photo} alt={partner.name} title={showTitle ? partner.name : undefined}
      className="avatar" style={style} onError={() => setErr(true)} />;
  }
  return <span className="avatar avatar-fallback" style={style} title={showTitle ? partner.name : undefined}>{partner.initials}</span>;
}

export function AvatarStack({ ids, partners, size = 24 }: { ids: string[]; partners: Record<string, Partner>; size?: number }) {
  return (
    <div className="avatar-stack">
      {ids.map((id, i) => (
        <span key={id} style={{ marginLeft: i ? -7 : 0, zIndex: ids.length - i, position: "relative" }}>
          <Avatar partner={partners[id] || null} size={size} />
        </span>
      ))}
    </div>
  );
}

const CLS_META: Record<string, { label: string; short: string }> = {
  regulatory: { label: "Regulatory", short: "REG" },
  tender: { label: "Tender", short: "TND" },
  operator: { label: "Operator", short: "OPR" },
  political: { label: "Political", short: "POL" },
  technology: { label: "Technology", short: "TEC" },
};

export function SignalTag({ cls, short, solid, style }: { cls: string; short?: boolean; solid?: boolean; style?: React.CSSProperties }) {
  const m = CLS_META[cls] || { label: cls, short: "" };
  return (
    <span className={clsx("sig-tag", "sig-" + cls, solid && "sig-solid")} style={style}>
      <span className="sig-dot" />
      {short ? m.short : m.label}
    </span>
  );
}

const HEAT_RANK: Record<string, number> = { critical: 3, high: 3, medium: 2, low: 1 };

export function HeatMeter({ level }: { level: Heat }) {
  const r = HEAT_RANK[level] || 1;
  return (
    <span className={clsx("heat", "heat-" + level)} title={"Heat: " + level}>
      {[1, 2, 3].map(n => <span key={n} className={clsx("heat-bar", n <= r && "on")} />)}
    </span>
  );
}

export function HeatPill({ level }: { level: Heat }) {
  const crit = level === "critical";
  return (
    <span className={clsx("heat-pill", "heat-" + level)}>
      {crit && <I.flame size={12} w={2} />}
      {level.charAt(0).toUpperCase() + level.slice(1)}
    </span>
  );
}

export function StageBadge({ stage }: { stage: Stage }) {
  const s = STAGES.find(s => s.id === stage);
  return <span className="stage-badge" data-stage={stage}>{s?.label || stage}</span>;
}

export function StageTrack({ stage }: { stage: Stage }) {
  const st = STAGES.filter(s => s.id !== "closed" as string);
  const idx = st.findIndex(s => s.id === stage);
  return (
    <div className="stage-track">
      {st.map((s, i) => (
        <div key={s.id} className={clsx("st-seg", i <= idx && "done", i === idx && "current")} title={s.label} />
      ))}
    </div>
  );
}

export function Pill({ className, style, onClick, children }: { className?: string; style?: React.CSSProperties; onClick?: () => void; children: React.ReactNode }) {
  return <span className={clsx("pill", className)} style={style} onClick={onClick}>{children}</span>;
}

export function Money({ v }: { v: number }) {
  return <span className="mono">${(v ?? 0).toFixed(1)}M</span>;
}

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="section-label">{children}</div>;
}

export function PartnerBadge({ partner }: { partner: { name: string; color: string; role?: string } | null }) {
  if (!partner) return null;
  return (
    <span className="inp-badge" style={{ "--inp": partner.color } as React.CSSProperties} title={partner.role}>
      <span className="inp-dot" />
      {partner.name}
    </span>
  );
}

export function Metric({ k, v, sub }: { k: string; v: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div className="metric">
      <div className="metric-k">{k}</div>
      <div className="metric-v">{v}</div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  );
}

const OWNER_LABEL: Record<string, string> = { geir: "Geir", trond: "Trond", bjorn: "Bjørn", all: "Alle", wait: "Vent" };
export { OWNER_LABEL };
